import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChatMarkdown } from './chat/ChatMarkdown';

// Import SVG assets
import LambotSvg from '../../../assets/lambot-icon.svg';
import SendIconSvg from '../../../assets/send-icon.svg';
import CloseIconSvg from '../../../assets/close-icon.svg';
import RefreshIconSvg from '../../../assets/refresh-icon.svg';
import InfoIconSvg from '../../../assets/info-icon.svg';

// ============================================================================
// Constants
// ============================================================================

const MAX_MESSAGE_LENGTH = 4000;
const CHAT_DISCLAIMER =
  'LAMBOt kan fouten maken. Controleer belangrijke informatie.';
const DEFAULT_WINDOW_WIDTH = 380;
const WELCOME_WINDOW_WIDTH = 400;
const DEFAULT_WINDOW_HEIGHT = 600;
const WELCOME_WINDOW_HEIGHT = 670;

// ============================================================================
// Types
// ============================================================================

type ChatMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  isStarter?: boolean;
  starterQuestions?: string[];
};

type DifyApiResponse = {
  answer?: string;
  conversation_id?: string;
  id?: string;
};

type DifyAppResponse = {
  conversation_opener?: string;
  opening_statement?: string;
  suggested_questions?: string[];
};

type DifyChatbotProps = {
  apiKey: string;
  appKey: string;
  apiBaseUrl: string;
  chapter: string;
};

// ============================================================================
// Helper Functions
// ============================================================================

const CHAT_STATE_KEY = 'dify_chat_state';
const KEEP_SESSION_KEY = 'dify_keep_session';
const getStorageKey = () => 'dify_conversation_global';

const getCurrentPageUrl = (): string => window.location.pathname;

type StoredChatState = {
  messages: ChatMessage[];
  isOpen: boolean;
  pageUrl: string;
};

type RestoredChat = {
  messages: ChatMessage[];
  isOpen: boolean;
  conversationId: string | null;
  /** True when the chat is open but has no messages yet (needs opener). */
  shouldLoadOpener: boolean;
};

const loadChatState = (): StoredChatState | null => {
  const raw = sessionStorage.getItem(CHAT_STATE_KEY);
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw);
    return {
      messages: Array.isArray(parsed.messages) ? parsed.messages : [],
      isOpen: parsed.isOpen === true,
      pageUrl: typeof parsed.pageUrl === 'string' ? parsed.pageUrl : '',
    };
  } catch {
    console.error('Failed to parse saved chatbot state from sessionStorage');
    return null;
  }
};

const saveChatState = (messages: ChatMessage[], isOpen: boolean): void => {
  sessionStorage.setItem(CHAT_STATE_KEY, JSON.stringify({
    messages,
    isOpen,
    pageUrl: getCurrentPageUrl(),
  } satisfies StoredChatState));
};

/** Keep the conversation when navigating via a link inside the chat. */
const keepSessionAcrossNextPage = (): void => {
  sessionStorage.setItem(KEEP_SESSION_KEY, 'true');
};

const shouldKeepSessionAcrossPage = (): boolean => {
  const keep = sessionStorage.getItem(KEEP_SESSION_KEY) === 'true';
  sessionStorage.removeItem(KEEP_SESSION_KEY);
  return keep;
};

/**
 * Restore chat for the current URL.
 * Same page (or link click from chat) → keep conversation.
 * Different page via normal navigation → fresh chat, keep open/closed.
 */
const restoreChatForCurrentPage = (): RestoredChat | null => {
  const saved = loadChatState();
  if (!saved) {
    return null;
  }

  const keepSession = shouldKeepSessionAcrossPage();
  const isSamePage = saved.pageUrl === getCurrentPageUrl();

  if (isSamePage || keepSession) {
    return {
      messages: saved.messages,
      isOpen: saved.isOpen,
      conversationId: sessionStorage.getItem(getStorageKey()),
      shouldLoadOpener: saved.isOpen && saved.messages.length === 0,
    };
  }

  sessionStorage.removeItem(getStorageKey());
  return {
    messages: [],
    isOpen: saved.isOpen,
    conversationId: null,
    shouldLoadOpener: saved.isOpen,
  };
};

/** If the user clicks an internal link in the chat, keep the session on the next page. */
const handleChatLinkClick = (event: React.MouseEvent): void => {
  const link = (event.target as HTMLElement).closest('a');
  if (!link?.href) {
    return;
  }

  try {
    const url = new URL(link.href, window.location.origin);
    if (url.origin === window.location.origin) {
      keepSessionAcrossNextPage();
    }
  } catch {
    // ignore invalid urls
  }
};

/**
 * We can derive chapter from URL structure.
 *
 * Some example url's:
 * http://localhost/online-uitgave/kunstboek-online-3-renaissance-revolutie/34-barok-1600-1720
 * http://localhost/online-uitgave/kunstboek-online-1-oudheid/13-grieken-ca-800-150-vchr
 * http://localhost/online-uitgave/kunstboek-online-6-postmodernisme-69-conceptkunst-vanaf-1960-joseph-beuys-vwo/leraar
 * http://localhost/online-uitgave/kunstboek-online-3-renaissance-revolutie-35-rococo-1720-1750/de-binnenkant-telt
 *
 * For now we'll assume that the first numberic value (can be two mutiple digits) after the last slash is the chapter.
 *
 * BE AWARE: This would break if the title of the online book has a number in it!
 *
 * @param url
 */
// Extracts the chapter number from the current page URL
const getChapter = (): string => {
  const url = getCurrentPageUrl();

  // Find the first occurrence of one or more digits, ignoring any '-' as negative sign.
  // This will match digits even if surrounded by dashes.
  // http://localhost/online-uitgave/kunstboek-online-7-hedendaags-73-damien-hirst-geb-1965-verenigd-koninkrijk/een-haai
  // Would result in '7'
  const match = url.match(/\d+/);
  if (match) {
    return match[0];
  }

  return '';
}

// ============================================================================
// Typing Indicator Component
// ============================================================================

const TypingIndicator = () => (
  <div className="chatbot-typing">
    <div className="chatbot-typing-dot"></div>
    <div className="chatbot-typing-dot"></div>
    <div className="chatbot-typing-dot"></div>
  </div>
);

const ChatDisclaimer = () => (
  <p className="chatbot-disclaimer" role="note">
    {CHAT_DISCLAIMER}
  </p>
);

// ============================================================================
// Main Component
// ============================================================================

export const DifyChatbot = ({ apiKey, appKey, apiBaseUrl }: DifyChatbotProps) => {
  // --------------------------------------------------------------------------
  // State
  // --------------------------------------------------------------------------
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [openerLoaded, setOpenerLoaded] = useState(false);
  const [windowWidth, setWindowWidth] = useState(DEFAULT_WINDOW_WIDTH);
  const [windowHeight, setWindowHeight] = useState(DEFAULT_WINDOW_HEIGHT);
  const [isResizing, setIsResizing] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [showFab, setShowFab] = useState(true);
  const [showWelcomeScreen, setShowWelcomeScreen] = useState(false);
  const [hasFirstOccurrenceOfConversationIdError, setHasFirstOccurrenceOfConversationIdError] = useState(false);

  const listRef = useRef<HTMLDivElement>(null);
  const windowRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // --------------------------------------------------------------------------
  // API Headers
  // --------------------------------------------------------------------------
  const headers = useMemo(() => ({
    'Authorization': `Bearer ${apiKey}`,
    'Content-Type': 'application/json'
  }), [apiKey]);

  // --------------------------------------------------------------------------
  // Callbacks
  // --------------------------------------------------------------------------

  /**
   * Send a message to Dify API
   */
  const sendMessageWithText = useCallback(async (messageText: string) => {
    if (!messageText.trim() || !apiKey) return;

    setError(null);
    setLoading(true);

    // Add user message to chat
    const userMessage: ChatMessage = {
      id: `${Date.now()}-user`,
      role: 'user',
      content: messageText.trim(),
    };
    setMessages(prev => [...prev, userMessage]);

    try {
      const current_page_url = getCurrentPageUrl();
      const chapter = getChapter();
      const body = {
        inputs: {
          current_page_url,
          chapter,
        },
        query: userMessage.content,
        response_mode: 'blocking',
        user: 'drupal-user',
        conversation_id: conversationId || undefined,
      } as Record<string, unknown>;

      const makeApiCall = async () => {
        const resp = await fetch(`${apiBaseUrl}/chat-messages`, {
          method: 'POST',
          headers,
          body: JSON.stringify(body),
        });

        if (!resp.ok) {
          const text = await resp.text();
          throw new Error(`API ${resp.status}: ${text}`);
        }

        return resp.json() as Promise<DifyApiResponse>;
      };

      try {
        const data = await makeApiCall();

        // Save conversation ID for continuity
        if (data.conversation_id && data.conversation_id !== conversationId) {
          setConversationId(data.conversation_id);
          sessionStorage.setItem(getStorageKey(), data.conversation_id);
        }

        // Add assistant response to chat
        const assistantMessage: ChatMessage = {
          id: `${Date.now()}-assistant`,
          role: 'assistant',
          content: data.answer || '',
        };
        setMessages(prev => [...prev, assistantMessage]);

        // Reset the error tracking state
        setHasFirstOccurrenceOfConversationIdError(false);
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : '';

        if (
          message.includes('"errors":{"conversation_id":"Existing conversation ID')
        ) {
          // Clear session storage for keys starting with "dify_conversation_/"
          Object.keys(sessionStorage).forEach(key => {
            if (key.startsWith('dify_conversation_/')) {
              sessionStorage.removeItem(key);
            }
          });

          // Reset the conversationId state variable
          setConversationId(null);

          // Add a message to inform the user that the chat has been refreshed.
          const refreshMessage: ChatMessage = {
            id: `${Date.now()}-assistant-reset`,
            role: 'assistant',
            content: 'De chatbot is zojuist vernieuwd, probeer het nog een keer.',
          };
          setMessages(prev => [...prev, refreshMessage]);

        } else {
          setError('LAMBOt is even niet bereikbaar. Probeer het opnieuw.');
        }
      }
    } catch (e: unknown) {
      console.error('[LAMBOt]', e);
      setError('LAMBOt is even niet bereikbaar. Probeer het opnieuw.');
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, apiKey, headers, conversationId]);

  /**
   * Send message from input field
   */
  const sendMessage = useCallback(async () => {
    if (!input.trim() || !apiKey) return;

    const messageToSend = input.trim().slice(0, MAX_MESSAGE_LENGTH);
    setInput(''); // Clear input immediately
    await sendMessageWithText(messageToSend);

    // Refocus input field after sending
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  }, [input, sendMessageWithText, apiKey]);

  /**
   * Fetch opening statement and suggested questions from Dify
   */
  const fetchConversationOpener = useCallback(async (force: boolean = false) => {
    if (!apiKey || !appKey) return;
    if (!force && openerLoaded) return;

    try {
      const resp = await fetch(`${apiBaseUrl}/parameters`, {
        method: 'GET',
        headers: headers,
      });

      if (!resp.ok) {
        throw new Error(`Failed to fetch app info: ${resp.status}`);
      }

      const data = await resp.json() as DifyAppResponse;

      if (data.opening_statement && data.opening_statement.trim()) {
        const messages: ChatMessage[] = [
          {
            id: 'opener-message',
            role: 'assistant',
            content: data.opening_statement.trim()
          }
        ];
        if (data.suggested_questions && data.suggested_questions.length > 0) {
          messages.push({
            id: 'opener-questions',
            role: 'assistant',
            content: '',
            starterQuestions: data.suggested_questions.map(q => q.trim())
          });
        }
        setMessages(messages);
      }
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : String(e);
      console.warn('Failed to fetch conversation opener:', message);
    } finally {
      setOpenerLoaded(true);
    }
  }, [apiKey, appKey, apiBaseUrl, openerLoaded, headers]);

  /**
   * Toggle chat window open/closed
   */
  const toggleChat = useCallback(() => {
    if (isOpen) {
      // Start closing animation
      setIsClosing(true);
      setShowFab(false); // Hide FAB during close
      // Wait for animation to complete before actually closing
      setTimeout(() => {
        setIsOpen(false);
        setIsClosing(false);
        setShowWelcomeScreen(false);
        // Show FAB after a short delay for smooth transition
        setTimeout(() => {
          setShowFab(true);
        }, 50);
      }, 300); // Match animation duration
    } else {
      // Check if user has seen welcome screen before
      const hasSeenWelcome = localStorage.getItem('lambot_welcome_seen') === 'true';

      // Open immediately and hide FAB
      setShowFab(false);
      setIsOpen(true);
      setIsClosing(false);

      if (!hasSeenWelcome) {
        // Show welcome screen for first-time users
        setShowWelcomeScreen(true);
      } else {
        // Load opener when opening chat for returning users
        if (!openerLoaded && messages.length === 0) {
          void fetchConversationOpener();
        }
      }
    }
  }, [isOpen, openerLoaded, messages.length, fetchConversationOpener]);

  /**
   * Reset conversation to start fresh
   */
  const handleReset = useCallback(() => {
    setMessages([]);
    setConversationId(null);
    setOpenerLoaded(false);
    sessionStorage.removeItem(getStorageKey());

    // Reload conversation opener after reset (force reload)
    setTimeout(() => {
      void fetchConversationOpener(true);
    }, 100);
  }, [fetchConversationOpener]);

  /**
   * Handle clicks on conversation starter buttons
   */
  const handleStarterClick = useCallback(async (questionText: string) => {
    await sendMessageWithText(questionText);
  }, [sendMessageWithText]);

  /**
   * Handle Enter key in input field
   */
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void sendMessage();
    }
  }, [sendMessage]);

  /**
   * Handle window resize - start
   */
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  /**
   * Handle window resize - move
   */
  const handleResizeMove = useCallback((e: MouseEvent) => {
    if (!isResizing || !windowRef.current) return;

    const rect = windowRef.current.getBoundingClientRect();
    const newWidth = Math.max(360, Math.min(800, window.innerWidth - e.clientX));
    const newHeight = Math.max(400, Math.min(window.innerHeight - 48, rect.height + (rect.top - e.clientY)));

    setWindowWidth(newWidth);
    setWindowHeight(newHeight);
  }, [isResizing]);

  /**
   * Handle window resize - end
   */
  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
  }, []);

  /**
   * Start chat from welcome screen
   */
  const handleStartChat = useCallback(() => {
    // Mark as seen only if this is the first time
    if (localStorage.getItem('lambot_welcome_seen') !== 'true') {
      localStorage.setItem('lambot_welcome_seen', 'true');
    }

    setShowWelcomeScreen(false);

    // Load opener only when starting chat for the first time
    if (!openerLoaded && messages.length === 0) {
      void fetchConversationOpener();
    }
  }, [openerLoaded, messages.length, fetchConversationOpener]);

  /**
   * Show welcome screen (info button)
   */
  const handleShowInfo = useCallback(() => {
    setShowWelcomeScreen(true);
  }, []);

  // --------------------------------------------------------------------------
  // Effects
  // --------------------------------------------------------------------------

  // Restore chat after page load (same page keeps history, new page starts fresh)
  useEffect(() => {
    const restored = restoreChatForCurrentPage();
    if (!restored) {
      return;
    }

    setIsOpen(restored.isOpen);

    if (restored.messages.length > 0) {
      setMessages(restored.messages);
      setOpenerLoaded(true);
    }

    if (restored.conversationId) {
      setConversationId(restored.conversationId);
    }

    if (restored.shouldLoadOpener) {
      void fetchConversationOpener(true);
    }
  }, []);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages]);

  // Handle resize mouse events
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
      document.body.style.cursor = 'nwse-resize';
      document.body.style.userSelect = 'none';

      return () => {
        document.removeEventListener('mousemove', handleResizeMove);
        document.removeEventListener('mouseup', handleResizeEnd);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
    }
  }, [isResizing, handleResizeMove, handleResizeEnd]);

  // Auto-focus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // Persist chat across reloads on the same page
  useEffect(() => {
    saveChatState(messages, isOpen);
  }, [messages, isOpen]);

  // --------------------------------------------------------------------------
  // Render
  // --------------------------------------------------------------------------

  // Show floating button when chat is closed
  if (!isOpen) {
    return (
      <div className="chatbot-opener">
        {showFab && (
          <button
            onClick={toggleChat}
            className="chatbot-fab"
            aria-label="Open chat"
          >
            <LambotSvg />
          </button>
        )}
      </div>
    );
  }

  // Show welcome screen for first-time users
  if (showWelcomeScreen) {
    return (
      <div
        ref={windowRef}
        className={`chatbot-window chatbot-welcome-screen ${isClosing ? 'chatbot-window--closing' : ''}`}
        style={{
          width: `${WELCOME_WINDOW_WIDTH}px`,
          height: `${WELCOME_WINDOW_HEIGHT}px`
        }}
      >
        {/* Header */}
        <div className="chatbot-header">
          <div className="chatbot-title-container">
            <div className="chatbot-icon">
              <LambotSvg />
            </div>
            <strong className="chatbot-title">Welkom bij LAMBOt!</strong>
          </div>
          <div className="chatbot-header-actions">
            <button
              onClick={toggleChat}
              className="chatbot-icon-button"
              aria-label="Chat sluiten"
              title="Sluiten"
            >
              <CloseIconSvg />
            </button>
          </div>
        </div>

        {/* Welcome content */}
        <div className="chatbot-welcome-content">
          <div className="chatbot-welcome-section">
            <h3>Ontdek LAMBO&apos;s experimentele AI-functie</h3>
            <p>
              Deze slimme AI-tool helpt je om het lesmateriaal beter te begrijpen. De LAMBOt bedenkt vragen en de LAMBO-redacteurs coachen en corrigeren de LAMBOt waar nodig. Let op: de LAMBOt (en soms zelfs de LAMBO-redactie) kan fouten maken. Die kun je doorgeven via de feedbackknop hieronder.
            </p>
          </div>

          <div className="chatbot-welcome-section">
            <ul>
              <li>📄 Oefen voor toetsen en examens: de LAMBOt stelt je vragen over voorstelling, vormgeving en inhoud.</li>
              <li>📊 Je krijgt uitgebreid feedback op je antwoord.</li>
              <li>💬 Stel je eigen vragen over de leerstof die je lastig vindt. Je kunt vragen stellen over de huidige uitgave.</li>
            </ul>
          </div>

          <div className="chatbot-welcome-actions">
            <button
              onClick={handleStartChat}
              className="chatbot-welcome-button"
            >
              Start chat
            </button>
            <a
              href="mailto:info@lambo.nl?subject=Feedback LAMBOt"
              className="chatbot-welcome-button chatbot-welcome-button--secondary"
            >
              Feedback geven
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Show full chat window when open
  return (
    <div
      ref={windowRef}
      className={`chatbot-window ${isResizing ? 'chatbot-window--resizing' : ''} ${isClosing ? 'chatbot-window--closing' : ''}`}
      style={{
        width: `${windowWidth}px`,
        height: `${windowHeight}px`
      }}
    >
      {/* Resize handle */}
      <div
        className="chatbot-resize-handle"
        onMouseDown={handleResizeStart}
        aria-label="Chat venster vergroten/verkleinen"
      />

      {/* Header with title and action buttons */}
      <div className="chatbot-header">
        <div className="chatbot-title-container">
          <div className="chatbot-icon">
            <LambotSvg />
          </div>
          <strong className="chatbot-title">LAMBOt</strong>
        </div>
        <div className="chatbot-header-actions">
          <button
            onClick={handleShowInfo}
            className="chatbot-icon-button"
            aria-label="Info tonen"
            title="Info"
          >
            <InfoIconSvg />
          </button>
          <button
            onClick={handleReset}
            className="chatbot-icon-button"
            aria-label="Gesprek herstarten"
            title="Gesprek herstarten"
          >
            <RefreshIconSvg />
          </button>
          <button
            onClick={toggleChat}
            className="chatbot-icon-button"
            aria-label="Chat sluiten"
            title="Sluiten"
          >
            <CloseIconSvg />
          </button>
        </div>
      </div>

      {/* Messages area */}
      <div ref={listRef} className="chatbot-messages" onClick={handleChatLinkClick}>
        {/* Loading state */}
        {messages.length === 0 && !openerLoaded && (
          <div className="chatbot-loading">Laden...</div>
        )}

        {/* Empty state */}
        {messages.length === 0 && openerLoaded && (
          <div className="chatbot-empty">Stel me vragen over deze pagina.</div>
        )}

        {/* Message list */}
        {messages.map(msg => (
          <div
            key={msg.id}
            className={`chatbot-message chatbot-message-${msg.role}`}
          >
            <div className={`chatbot-bubble chatbot-bubble-${msg.role}`}>
              {msg.starterQuestions ? (
                <div className="conversation-starters">
                  {msg.starterQuestions.map((question, idx) => (
                    <button
                      key={idx}
                      className="conversation-starter"
                      onClick={() => handleStarterClick(question)}
                    >
                      {question}
                    </button>
                  ))}
                </div>
              ) : msg.isStarter ? (
                <div
                  className="conversation-starter"
                  onClick={() => handleStarterClick(msg.content)}
                >
                  {msg.content}
                </div>
              ) : (
                <ChatMarkdown content={msg.content} />
              )}
            </div>
          </div>
        ))}

        {/* Typing indicator while AI is responding */}
        {loading && (
          <div className="chatbot-message chatbot-message-assistant">
            <div className="chatbot-bubble chatbot-bubble-assistant">
              <TypingIndicator />
            </div>
          </div>
        )}

        {/* Error message */}
        {error && !hasFirstOccurrenceOfConversationIdError && (
          <div className="chatbot-error">{error}</div>
        )}

        <ChatDisclaimer />
      </div>

      {/* Input area */}
      <div className="chatbot-input-container">
        <textarea
          ref={inputRef}
          value={input}
          onChange={e => {
            setInput(e.target.value);
            const textarea = e.target;
            textarea.style.height = 'auto';
            const style = window.getComputedStyle(textarea);
            const paddingTop = parseFloat(style.paddingTop);
            const paddingBottom = parseFloat(style.paddingBottom);
            const borderTop = parseFloat(style.borderTopWidth);
            const borderBottom = parseFloat(style.borderBottomWidth);
            const verticalPaddingAndBorder = paddingTop + paddingBottom + borderTop + borderBottom;
            const lineHeight = parseFloat(style.lineHeight);
            const maxScrollHeight = (5 * lineHeight) + verticalPaddingAndBorder;

            if (textarea.scrollHeight > maxScrollHeight) {
              textarea.style.overflowY = 'scroll';
              textarea.style.height = `${maxScrollHeight}px`;
            } else {
              textarea.style.overflowY = 'hidden';
              textarea.style.height = `${textarea.scrollHeight}px`;
            }
          }}
          onKeyDown={handleKeyDown}
          placeholder="Typ je bericht..."
          className="chatbot-input"
          aria-label="Chat bericht invoer"
          disabled={loading}
          rows={1}
          style={{ resize: 'none', overflowY: 'hidden' }}
          maxLength={MAX_MESSAGE_LENGTH}
        />
        <button
          disabled={loading || !apiKey || !input.trim()}
          onClick={() => void sendMessage()}
          className="chatbot-send-button"
          aria-label={loading ? 'Bericht verzenden' : 'Bericht verzenden'}
          title="Verzenden"
        >
          {loading ? (
            <div className="chatbot-spinner" />
          ) : (
            <SendIconSvg />
          )}
        </button>
      </div>
    </div>
  );
};

export default DifyChatbot;


